import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Button,
  Paper,
  Divider
} from '@mui/material';
import { templateService } from '../../services/templateService';
import { FrameworkTemplate } from '../../models/types';
import TemplateList from './TemplateList';
import TemplateEditor from './TemplateEditor';

interface AdminPageProps {
  onBack: () => void;
}

const AdminPage: React.FC<AdminPageProps> = ({ onBack }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [templates, setTemplates] = useState<FrameworkTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<FrameworkTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    // Initialize templates if needed
    templateService.initializeIfEmpty();
    
    // Load templates
    loadTemplates();
  }, []);

  const loadTemplates = () => {
    const allTemplates = templateService.getAllTemplates();
    setTemplates(allTemplates);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleTemplateSelect = (template: FrameworkTemplate) => {
    setSelectedTemplate(template);
    setIsEditing(true);
  };

  const handleCreateTemplate = (name: string, description: string) => {
    const newTemplate = templateService.createNewTemplate(name, description);
    setTemplates([...templates, newTemplate]);
    setSelectedTemplate(newTemplate);
    setIsEditing(true);
  };

  const handleCloneTemplate = (id: string, newName: string) => {
    const clonedTemplate = templateService.cloneTemplate(id, newName);
    if (clonedTemplate) {
      loadTemplates();
      setSelectedTemplate(clonedTemplate);
      setIsEditing(true);
    }
  };

  const handleDeleteTemplate = (id: string) => {
    templateService.deleteTemplate(id);
    loadTemplates();
    
    if (selectedTemplate && selectedTemplate.id === id) {
      setSelectedTemplate(null);
      setIsEditing(false);
    }
  };

  const handleSaveTemplate = (template: FrameworkTemplate) => {
    templateService.saveTemplate(template);
    loadTemplates();
    setSelectedTemplate(template);
  };

  const handleBackToList = () => {
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center'
      }}>
        <Typography variant="h5" component="h1">
          Administration
        </Typography>
        <Button variant="outlined" onClick={onBack}>
          Back to Dashboard
        </Button>
      </Box>
      
      <Divider />
      
      <Paper sx={{ m: 2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label="Framework Templates" />
          <Tab label="Weight Profiles" disabled />
          <Tab label="User Management" disabled />
        </Tabs>
      </Paper>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {tabIndex === 0 && (
          <>
            {isEditing && selectedTemplate ? (
              <TemplateEditor 
                template={selectedTemplate}
                onSave={handleSaveTemplate}
                onBack={handleBackToList}
              />
            ) : (
              <TemplateList 
                templates={templates}
                onSelect={handleTemplateSelect}
                onCreate={handleCreateTemplate}
                onClone={handleCloneTemplate}
                onDelete={handleDeleteTemplate}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminPage;
